import React, { type HTMLAttributes, type KeyboardEvent } from 'react';
import { cn } from './cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Adds interactive hover affordance (for clickable cards). */
  interactive?: boolean;
}

/** Raised surface container with subtle border and shadow. */
export function Card({
  interactive,
  className,
  onClick,
  onKeyDown,
  role,
  tabIndex,
  ...rest
}: CardProps) {
  // A card is keyboard-activatable when it is both interactive AND wired with
  // an onClick handler. In that case it behaves like a button: focusable and
  // activated by Enter/Space. Callers can still override role/tabIndex.
  const clickable = interactive && typeof onClick === 'function';

  const handleKeyDown = clickable
    ? (e: KeyboardEvent<HTMLDivElement>) => {
        onKeyDown?.(e);
        if (e.defaultPrevented) return;
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
        }
      }
    : onKeyDown;

  return (
    <div
      className={cn(
        'rounded-2xl border border-surface-border bg-surface-2 shadow-card',
        interactive &&
          'cursor-pointer transition-colors hover:border-zinc-600 hover:bg-surface-3',
        className,
      )}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={clickable ? (role ?? 'button') : role}
      tabIndex={clickable ? (tabIndex ?? 0) : tabIndex}
      {...rest}
    />
  );
}

export function CardHeader({
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-3 border-b border-surface-border px-5 py-4',
        className,
      )}
      {...rest}
    />
  );
}

export function CardTitle({
  className,
  ...rest
}: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={cn('text-sm font-semibold text-zinc-100', className)}
      {...rest}
    />
  );
}

export function CardBody({
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 py-4', className)} {...rest} />;
}

export function CardFooter({
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-2 border-t border-surface-border px-5 py-3',
        className,
      )}
      {...rest}
    />
  );
}
